/**
 * Componente de mensaje individual del chat
 */

import { useState, type ReactNode } from 'react';
import { ChatMessage as ChatMessageType } from '../../types';
import { translateMessage } from '../../services/translationService';
import './ChatMessage.css';

function TranslateIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      role="img"
      aria-hidden="true"
      style={{ clipPath: 'circle(50%)', display: 'block' }}
    >
      <rect x="0" y="0" width="20" height="10" fill="#FCD116" />
      <rect x="0" y="10" width="20" height="5" fill="#003893" />
      <rect x="0" y="15" width="20" height="5" fill="#CE1126" />
    </svg>
  );
}

/**
 * Renderiza la traducción soportando:
 *   - **texto** → <strong>texto</strong>
 *   - « texto » se resalta como término francés (clase fr-term)
 * No se usa una librería de markdown completa para mantenerlo ligero
 * y evitar inyección de HTML.
 */
function renderTranslation(text: string): ReactNode {
  // Token regex: captura **bold**, « ... », o cualquier otro tramo
  const tokenRegex = /(\*\*[^*]+\*\*|«[^»]+»)/g;
  const parts = text.split(tokenRegex);
  return parts.map((part, i) => {
    if (!part) return null;
    if (part.startsWith('**') && part.endsWith('**')) {
      const inner = part.slice(2, -2);
      // Si la negrita está dentro de unas comillas francesas adyacentes, el
      // tramo « ... » externo aplicará su propio estilo en el siguiente token.
      return <strong key={i} className="fr-term-strong">{inner}</strong>;
    }
    if (part.startsWith('«') && part.endsWith('»')) {
      // Renderizar el contenido de las comillas como término francés con
      // resalte sutil; permitir negrita anidada.
      const innerRaw = part.slice(1, -1).trim();
      const innerParts = innerRaw.split(/(\*\*[^*]+\*\*)/g).map((seg, j) => {
        if (seg.startsWith('**') && seg.endsWith('**')) {
          return <strong key={j}>{seg.slice(2, -2)}</strong>;
        }
        return seg;
      });
      return (
        <span key={i} className="fr-term">
          <span className="fr-quote" aria-hidden="true">«&nbsp;</span>
          {innerParts}
          <span className="fr-quote" aria-hidden="true">&nbsp;»</span>
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

/**
 * Renderiza el contenido del mensaje convirtiendo URLs (http/https) en enlaces
 * clicables que abren en pestaña nueva. Necesario para las recomendaciones de
 * Culturethèque (2026-08): el estudiante recibe el enlace del recurso y debe
 * poder abrirlo. Sin librería de markdown: se construyen elementos React, sin
 * riesgo de inyección de HTML.
 */
const URL_IN_TEXT_RE = /(https?:\/\/[^\s<>«»"')\]]+[^\s<>«»"')\].,;:!?])/g;

function renderWithLinks(text: string): ReactNode {
  const parts = text.split(URL_IN_TEXT_RE);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="message-link"
        onClick={(e) => e.stopPropagation()}
      >
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

interface ChatMessageProps {
  message: ChatMessageType;
  onClick?: (message: ChatMessageType) => void;
  onCopy?: (message: ChatMessageType) => void;
}

export function ChatMessage({ message, onClick, onCopy }: ChatMessageProps) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const [translation, setTranslation] = useState<string | null>(null);
  const [showTranslation, setShowTranslation] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);

  // Mostrar contenido sin los prefijos [audio] / [text].
  // (Los prefijos [fr]/[es] del STT se eliminaron — el modelo detecta el
  // idioma directamente del contenido y activa el andamiaje sin marker.)
  const rawContent = message.content || '';
  const displayContent = rawContent.replace(/^\[(audio|text)\]\s*/i, '');

  // Determinar si la burbuja es clickeable (tiene audio o es para copiar)
  const isClickable = (isAssistant && message.audioUrl) || onClick || onCopy;

  const handleClick = () => {
    if (onClick) {
      onClick(message);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    // Click derecho para copiar
    if (onCopy) {
      e.preventDefault();
      onCopy(message);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Ctrl+C o Cmd+C para copiar
    if ((e.ctrlKey || e.metaKey) && e.key === 'c' && onCopy) {
      e.preventDefault();
      onCopy(message);
    }
  };

  const handleTranslate = async (e: React.MouseEvent) => {
    e.stopPropagation();

    // Si ya se tradujo, solo toggle visibilidad
    if (translation) {
      setShowTranslation(!showTranslation);
      return;
    }

    setIsTranslating(true);
    try {
      const result = await translateMessage(displayContent);
      setTranslation(result);
      setShowTranslation(true);
    } catch {
      // Silenciar error — el botón simplemente no hace nada
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <div className={`chat-message ${isUser ? 'user' : 'assistant'}`}>
      {isAssistant && (
        <img
          src="/assets/ui/avatares/Galia_.png"
          alt="Galia"
          className="mini-avatar-galia"
        />
      )}
      <div className="message-bubble-wrapper">
        <div
          className={`message-bubble ${isUser ? 'user-bubble' : 'assistant-bubble'} ${isClickable ? 'clickable' : ''}`}
          onClick={handleClick}
          onContextMenu={handleContextMenu}
          onKeyDown={handleKeyDown}
          tabIndex={isClickable ? 0 : -1}
          role={isClickable ? "button" : undefined}
          aria-label={isAssistant && message.audioUrl ? 'Mensaje con audio. Click para reproducir, Ctrl+C para copiar' : isClickable ? 'Mensaje. Click derecho o Ctrl+C para copiar' : ''}
          title={isAssistant && message.audioUrl ? 'Click para reproducir audio | Ctrl+C para copiar' : isClickable ? 'Click derecho o Ctrl+C para copiar' : ''}
        >
          <p className="message-text">{renderWithLinks(displayContent)}</p>
          {isAssistant && message.audioUrl && (
            <span className="audio-indicator" aria-label="Tiene audio">🔊</span>
          )}
          {isAssistant && displayContent.trim().length > 0 && (
            <button
              className={`translate-button ${isTranslating ? 'translating' : ''} ${showTranslation ? 'active' : ''}`}
              onClick={(e) => { e.stopPropagation(); handleTranslate(e); }}
              disabled={isTranslating}
              aria-label={showTranslation ? 'Ocultar traducción' : 'Ver traducción al español'}
              title={showTranslation ? 'Ocultar traducción' : 'Ver traducción'}
            >
              <TranslateIcon size={20} />
            </button>
          )}
        </div>
        {isAssistant && showTranslation && translation && (
          <div className="translation-bubble">
            <p className="message-translation">{renderTranslation(translation)}</p>
          </div>
        )}
      </div>
    </div>
  );
}


